import { api_fetch, HttpError, HttpResponse } from "@/api/client"
import { Result } from "@/utils"

export interface CustomerPricingRow {
  price_id: string
  product_id: string
  product_name: string
  product_sku?: string
  customer_name: string
  retailer_name?: string
  unit_price: number
  currency: string
  effective_start?: string
  effective_end?: string
  price_type?: string
  different_older_count: number
}

export interface SetCustomerPricingRequest {
  customer_name: string
  retailer_name?: string
  /** Price every active product in this line… */
  product_line_id?: string
  /** …or exactly these products. */
  product_ids?: string[]
  unit_price: number
  /** When set, prior open prices close the day before and the new price starts here. */
  effective_start?: string
  effective_end?: string
  price_type?: string
}

export interface SetCustomerPricingResponse {
  products_priced: string[]
  rows_created: number
  rows_updated: number
  rows_closed: number
}

export interface CustomerPricingListResponse {
  items: CustomerPricingRow[]
  total: number
}

export interface CustomerPricingHistoryEntry {
  unit_price: number
  currency: string
  effective_start?: string
  effective_end?: string
  price_type?: string
  source?: string
}

export async function searchCustomerPricing(params: {
  q?: string
  customer?: string
  product?: string
  limit?: number
  offset?: number
}): Promise<Result<HttpResponse<CustomerPricingListResponse>, HttpError>> {
  return api_fetch<CustomerPricingListResponse>("/product-catalog/customer-pricing", {
    params,
  })
}

export async function listCustomerPricingCustomers(): Promise<Result<HttpResponse<string[]>, HttpError>> {
  return api_fetch<string[]>("/product-catalog/customer-pricing/customers", { use_cache: true })
}

export async function listCustomerPricingHistory(params: {
  product_id: string
  customer_name: string
  retailer_name?: string
}): Promise<Result<HttpResponse<CustomerPricingHistoryEntry[]>, HttpError>> {
  return api_fetch<CustomerPricingHistoryEntry[]>("/product-catalog/customer-pricing/history", {
    params,
  })
}

export async function setCustomerPricing(
  request: SetCustomerPricingRequest
): Promise<Result<HttpResponse<SetCustomerPricingResponse>, HttpError>> {
  return api_fetch<SetCustomerPricingResponse>("/product-catalog/customer-pricing", {
    method: "POST",
    body: request,
  })
}

export async function deleteCustomerPrice(
  price_id: string
): Promise<Result<HttpResponse<{ ok: boolean }>, HttpError>> {
  return api_fetch<{ ok: boolean }>(`/product-catalog/customer-pricing/${price_id}`, {
    method: "DELETE",
  })
}

export function formatPricingDate(value?: string): string {
  if (!value) return "Open"
  const [year, month, day] = value.split("-")
  if (!year || !month || !day) return value
  return `${month}/${day}/${year}`
}

export function formatUnitPrice(amount: number, currency: string): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency || "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount)
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  describe("formatPricingDate", () => {
    it("formats ISO dates for display", () => {
      expect(formatPricingDate("2026-01-01")).toBe("01/01/2026")
      expect(formatPricingDate()).toBe("Open")
    })
  })
}
